import { ConfigurationKey } from "@salon/types";
import { useCallback, useRef } from "react";
import { useConfigurationSubscription } from "./use-configuration-subscription";

interface UseDebouncedConfigSubscriptionProps {
  salonId: string;
  keys?: ConfigurationKey[];
  categories?: string[];
  enabled?: boolean;
  onConfigurationChange?: (changedKeys: string[]) => void;
  debounceMs?: number;
}

/**
 * Hook that provides debounced configuration subscription updates
 * to prevent rapid-fire updates from causing infinite loops
 */
export function useDebouncedConfigSubscription({
  salonId,
  keys,
  categories,
  enabled = true,
  onConfigurationChange,
  debounceMs = 300,
}: UseDebouncedConfigSubscriptionProps) {
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingChangesRef = useRef<Set<string>>(new Set());

  // Debounced callback that batches multiple changes
  const debouncedCallback = useCallback(
    (changedKeys: string[]) => {
      // Add new changes to pending set
      changedKeys.forEach((key) => {
        pendingChangesRef.current.add(key);
      });

      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new timer
      debounceTimerRef.current = setTimeout(() => {
        if (pendingChangesRef.current.size > 0) {
          const allChangedKeys = Array.from(pendingChangesRef.current);
          pendingChangesRef.current.clear();

          onConfigurationChange?.(allChangedKeys);
        }
        debounceTimerRef.current = null;
      }, debounceMs);
    },
    [onConfigurationChange, debounceMs]
  );

  // Use the regular subscription hook with debounced callback
  const subscriptionResult = useConfigurationSubscription({
    salonId,
    keys,
    categories,
    enabled,
    onConfigurationChange: debouncedCallback,
  });

  // Cleanup timer on unmount
  const cleanup = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    pendingChangesRef.current.clear();
  }, []);

  // Return the subscription result with cleanup function
  return {
    ...subscriptionResult,
    cleanup,
  };
}
